import type {
    ContentLink,
    ContentInlineVideoProps,
    ContentVideoSource,
    ThemeItem,
    ThemeType,
    ContentImage,
    ContentBlock,
    BlockType,
    ContentResourceLocation,
    BreakpointType,
} from './content';
import {
    ContentBlockThemeStyleItems
} from './content';
import type {
    RoundedValueCustom
} from 'x1-base/types';
import type {
    FullImage
} from '~/types';



export type ContentBlockButtonTypeLink = 'link' | 'external' | 'youtube' | 'video' | 'image';

export interface ContentBlockCustomIcon {
    icon?: ThemeItem<ContentResourceLocation>;
}

export interface ContentBlockTextTitle extends ContentBlockCustomIcon {
    content?: Nullable<string>;
}

export interface ContentBlockTextSubTitle extends ContentBlockCustomIcon {
    content?: Nullable<string>;
}

export interface ContentBlockTextCaption extends ContentBlockCustomIcon {
    content?: Nullable<string>;
}

export interface ContentBlockTextBody extends ContentBlockCustomIcon {
    content?: Nullable<string>;
}

export interface ContentBlockTextButtonItem extends ContentLink {
    type: ContentBlockButtonTypeLink;
    content: Nullable<string>;
    theme: ThemeItem<ContentBlockThemeStyleItems>;
}

export interface ContentBlockTextButton {
    items: ContentBlockTextButtonItem[];
}

// Text Block
export interface ContentBlockText {
    title: ContentBlockTextTitle;
    subTitle?: ContentBlockTextSubTitle;
    caption?: ContentBlockTextCaption;
    body: ContentBlockTextBody;
    buttonsCssClass?: string[];
    buttons: ContentBlockTextButton;
    showUnderlay?: boolean;
}

// Image Block
export interface ContentBlockImageStaticConfigBase {
    altText: string;
    plainImage?: boolean;
    contain?: boolean;
    autoResponsive?: boolean;
    fluidWidth?: boolean;
    fluidHeight?: boolean;
    fillHeight?: boolean;
    fillWidth?: boolean;
    position?: string;
    rounded?: RoundedValueCustom;
}

// Video Block
export interface ContentBlockVideoStaticConfigBase extends ContentBlockImageStaticConfigBase {
    videoUrlDesktop: string;
    videoUrlMobile: string;
}

export interface ContentBlockImageStatic {
    base: Partial<ContentBlockImageStaticConfigBase>;
    breakpoint?: PartialRecord<BreakpointType, Partial<ContentBlockImageStaticConfigBase>>;
    theme?: ThemeItem<ContentImage>;
    component?: {
        breakpoint?: PartialRecord<BreakpointType, ContentBlockThemeStyleItems>;
    }
}

export interface ContentBlockVideoStatic {
    base: Partial<ContentBlockVideoStaticConfigBase>;
    breakpoint?: PartialRecord<BreakpointType, Partial<ContentBlockVideoStaticConfigBase>>;
    theme?: ThemeItem<ContentImage>;
    component?: {
        breakpoint?: PartialRecord<BreakpointType, ContentBlockThemeStyleItems>;
    }
}

// Inline Video Block
export interface ContentBlockInlineVideo extends ContentLink, ContentVideoSource {
    video: ContentInlineVideoProps;
}

// Blocks

export type ContentBlockType = 'text' | 'image-static' | 'video-static' | 'inline-video';

export type ContentBlockConfigType = ContentBlockText | ContentBlockImageStatic | ContentBlockVideoStatic | ContentBlockInlineVideo;

// Block Layout Type

export enum LayoutTypeForFeatures {
    HALF = 'half',
    VERTICAL = 'vertical',
    HORIZONTAL = 'horizontal',
    STACKED = 'stacked'
}


export interface ContentBlockCommonProps {
    theme: ThemeType;
    breakpoint: string;
    breakpointName: BreakpointType;
    isHyperlinkBlock?: boolean;
    isMobile: boolean;
}

export interface ContentBlockComponentProps extends ContentBlockCommonProps {
    block: ContentBlock;
}

export interface ContentSubBlockComponentProps extends ContentBlockCommonProps {
    id?: string;
    subBlock: BlockType;
}

export interface ContentBlocksComponentProps<T> extends ContentBlockCommonProps {
    config: T;
}


// - - - - - Content Blocks V2 - - - - -

export type ContentBlockRenderedHeight = 'auto' | 'small' | 'medium' | 'large';
export type ContentBlockRenderedHostedVideoService = 'youtube' | 'vimeo';

export interface ContentBlockRenderedLink {
    kind: 'external' | 'page' | 'product' | 'category';
    target: string;
    url: Nullable<string>;
}

export interface ContentBlockRenderedVideo {
    url: string;
    altText?: string;
}

export interface ContentBlockRenderedHostedVideo {
    service: ContentBlockRenderedHostedVideoService;
    id: string;
}


/**
 * text/markdown/choice slots → string; number slots → number;
 * video slots → file URL string; group slots → nested object;
 * list slots → array of the `of` slot's value type.
 */
export type ContentBlockRenderedValue =
    | string
    | number
    | boolean
    | FullImage
    | ContentBlockRenderedLink
    | ContentBlockRenderedValue[]
    | { [slotKey: string]: ContentBlockRenderedValue }
    | null;


export interface ContentBlockRenderedTemplate {
    ref: string;
    version: number;
}

export interface ContentBlockRendered<TValues = Record<string, ContentBlockRenderedValue>> {
    schema: string;
    schemaVersion: number;
    template: ContentBlockRenderedTemplate | null;
    placements: string[];
    values: TValues;
}

export interface SplitFeatureValues {
    heading: string;
    body: string;
    mediaSide: 'left' | 'right';
    height: ContentBlockRenderedHeight;
    image?: FullImage;
    altImage?: FullImage;
    videoLg?: ContentBlockRenderedVideo;
    videoSm?: ContentBlockRenderedVideo;
    videoHosted?: ContentBlockRenderedHostedVideo;
}

export interface ProductFeatureHalfSplitV1 extends ContentBlockRendered<SplitFeatureValues> {
    schema: 'product_feature_half_split';
    schemaVersion: 1;
    template: {
        ref: 'HalfSplitFeature';
        version: 1
    };
}

export interface ProductFeatureXSplitV1 extends ContentBlockRendered<SplitFeatureValues> {
    schema: 'product_feature_x_split';
    schemaVersion: 1;
    template: {
        ref: 'XSplitFeature';
        version: 1
    };
}

export interface ProductFeatureYSplitV1 extends ContentBlockRendered<SplitFeatureValues> {
    schema: 'product_feature_y_split';
    schemaVersion: 1;
    template: {
        ref: 'YSplitFeature';
        version: 1
    };
}

export interface ProductFeatureZSplitV1 extends ContentBlockRendered<SplitFeatureValues> {
    schema: 'product_feature_z_split';
    schemaVersion: 1;
    template: {
        ref: 'ZSplitFeature';
        version: 1
    };
}

/** Any rendered Content Block currently served by the CMS. */
export type RenderedContentBlock =
    | ProductFeatureHalfSplitV1
    | ProductFeatureXSplitV1
    | ProductFeatureYSplitV1
    | ProductFeatureZSplitV1;


